import { FC } from 'react'

import { Handle, HandleType, Position } from '@xyflow/react'
import { useAtomValue } from 'jotai'
import { match } from 'ts-pattern'

import {
  GreyGrey40,
  GreyWhite,
  IndigoIndigo50,
  RedRed60
} from '@sequencehq/core-components'

import {
  edgesAtom,
  isEditingDisabledAtom,
  selectedEdgesAtom
} from 'Automations/automationEditor/atoms'
import { getNodeHandleId } from '../helpers'
import { useHandleHasError } from '../validation/hooks/useHandleHasError'

type Props = {
  nodeId: string
  handleMapping?: string
  type: HandleType
  position: Position
  selected: boolean
  insideRow?: boolean
}

export const NodeHandle: FC<Props> = ({
  nodeId,
  handleMapping,
  type,
  position,
  selected: selectedProp,
  insideRow
}) => {
  const edges = useAtomValue(edgesAtom)
  const selectedEdges = useAtomValue(selectedEdgesAtom)
  const isEditingDisabled = useAtomValue(isEditingDisabledAtom)

  const handleId = getNodeHandleId({
    nodeId,
    type,
    handleMapping
  })

  const isEdgeSelected: boolean = !!selectedEdges.find(edge => {
    return edge.sourceHandle === handleId || edge.targetHandle === handleId
  })

  const hasConnectedEdge =
    type === 'target' && edges.some(edge => edge.targetHandle === handleId)

  const selected: boolean = selectedProp || isEdgeSelected

  const hasError = useHandleHasError({ nodeId, handleId })

  return (
    <Handle
      type={type}
      position={position}
      id={handleId}
      isConnectable={!isEditingDisabled}
      isConnectableStart={!hasConnectedEdge}
      data-testid={`automations.editor.node.handle.${handleId}`}
      data-error={hasError}
      style={{
        width: '14px',
        height: '14px',
        border: match({ selected, hasError })
          .with(
            { selected: true, hasError: true },
            () => `${RedRed60} 2px solid`
          )
          .with(
            { selected: true, hasError: false },
            () => `${IndigoIndigo50} 2px solid`
          )
          .with(
            { selected: false, hasError: true },
            () => `${RedRed60} 2px solid`
          )
          .with(
            { selected: false, hasError: false },
            () => `${GreyGrey40} 1.5px solid`
          )
          .exhaustive(),
        outline: `${GreyWhite} 2px solid`,
        background: GreyWhite,
        right: insideRow ? '-12px' : '0px'
      }}
    />
  )
}
